import React from 'react';
import clsx from 'clsx';
import { WebWorkerImage } from '@components/basics/Images/WebWorkerImage';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import type { FragmentType, PlaylistCoverImagesFragment } from '@graphql/reactQuery';
import { getFragment, gql, PlaylistCoverImagesFragmentDoc, ShelfType } from '@graphql/reactQuery';
import { COVER_IMAGE_BACKGROUND_SRC, COVER_IMAGE_DOMINANT_COLOR } from '@utils/constants';

const LIKED_SOUNDS_BACKGROUND_SRC = '/images/liked-sounds-background.svg';
const LIKED_SOUNDS_COVER_SRC = '/images/liked-sounds-cover-new.png';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment PlaylistCoverImages on Shelf {
    id
    type
    coverImage {
      id
      url
      dominantColor
    }
    previewReleases {
      id
      release {
        id
        coverImage {
          id
          url
          dominantColor
        }
        staticCoverImage {
          id
          url
          dominantColor
        }
      }
      ownedGoldenEgg {
        id
        goldenEggImage {
          id
          url
        }
        animatedGoldenEggImageOptimized {
          id
          url
        }
      }
    }
  }
`);

export const getPlaylistBackgroundImageUrl = (
  playlist: FragmentType<PlaylistCoverImagesFragmentDoc>,
) => {
  const { type, coverImage, previewReleases } = getFragment(
    PlaylistCoverImagesFragmentDoc,
    playlist,
  );

  if (type === ShelfType.UserLikedSounds) return LIKED_SOUNDS_BACKGROUND_SRC;
  if (coverImage) return coverImage.url;
  if (!previewReleases || !previewReleases[0]) return COVER_IMAGE_BACKGROUND_SRC;
  const firstRelease = previewReleases[0].release;
  return firstRelease.staticCoverImage?.url ?? firstRelease.coverImage.url;
};

export const getPlaylistBackgroundDominantColor = (
  playlist: FragmentType<PlaylistCoverImagesFragmentDoc>,
) => {
  const { coverImage, previewReleases } = getFragment(PlaylistCoverImagesFragmentDoc, playlist);

  if (coverImage) return coverImage.dominantColor;
  if (!previewReleases || !previewReleases[0]) return COVER_IMAGE_DOMINANT_COLOR;
  const firstRelease = previewReleases[0].release;
  return firstRelease.staticCoverImage?.dominantColor ?? firstRelease.coverImage.dominantColor;
};

const CoverImage = ({
  size,
  previewRelease,
  priority,
}: {
  size: number;
  previewRelease: PlaylistCoverImagesFragment['previewReleases'][number];
  priority: boolean;
}) => {
  const { release, ownedGoldenEgg } = previewRelease;

  return !!ownedGoldenEgg ? (
    <WebWorkerImage
      src={
        ownedGoldenEgg.animatedGoldenEggImageOptimized?.url ||
        ownedGoldenEgg.goldenEggImage?.url ||
        release?.coverImage?.url
      }
      width={size}
      height={size}
    />
  ) : (
    <NextFutureImage
      alt="playlist-cover-img"
      src={release?.staticCoverImage?.url || release?.coverImage?.url}
      width={size}
      height={size}
      style={{
        objectFit: 'cover',
        objectPosition: 'center',
      }}
      priority={priority}
    />
  );
};

export const CoverImages = ({
  playlist,
  maxSize,
  priority,
}: {
  playlist: FragmentType<PlaylistCoverImagesFragmentDoc>;
  maxSize: number;
  priority: boolean;
}) => {
  const { type, coverImage, previewReleases } = getFragment(
    PlaylistCoverImagesFragmentDoc,
    playlist,
  );

  const isLikedSounds = type === ShelfType.UserLikedSounds;
  const hasCoverImage = !!coverImage;
  const releaseCount = previewReleases.length;

  const shouldShowReleaseImages = !isLikedSounds && !hasCoverImage;
  const shouldShowGrid = shouldShowReleaseImages && releaseCount > 1;
  // backgrouhd won't be visible if the grid is full or if there is only one image that fills the container so don't bother rendering
  const shouldShowDefaultBackground =
    shouldShowReleaseImages && releaseCount !== 1 && releaseCount < 4;

  let backgroundUrl = null;
  if (isLikedSounds) {
    backgroundUrl = LIKED_SOUNDS_COVER_SRC;
  } else if (hasCoverImage) {
    backgroundUrl = coverImage.url;
  } else if (shouldShowDefaultBackground) {
    backgroundUrl = COVER_IMAGE_BACKGROUND_SRC;
  }

  return (
    <div
      className={clsx('relative h-full w-full overflow-hidden rounded-md bg-black', {
        'grid grid-cols-2': shouldShowGrid,
      })}
    >
      {backgroundUrl && (
        <NextFutureImage
          className={clsx(
            shouldShowDefaultBackground && 'absolute left-0 top-0 z-below h-full w-full',
            'bg-cover-skeleton',
          )}
          alt="Playlist cover background"
          src={backgroundUrl}
          width={maxSize}
          height={maxSize}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          priority={priority}
        />
      )}
      {shouldShowReleaseImages && (
        <>
          {previewReleases.map((previewRelease, index) => (
            <div
              key={previewRelease.id}
              className={clsx(
                { 'col-start-2 row-start-2': releaseCount === 2 && index === 1 },
                'bg-cover-skeleton',
              )}
            >
              <CoverImage
                previewRelease={previewRelease}
                size={releaseCount === 1 ? maxSize : maxSize / 2}
                priority={priority}
              />
            </div>
          ))}
        </>
      )}
    </div>
  );
};
